/**
 * Detalhe da atividade.
 *
 * O botão de concluir chama a MESMA ação do `✓` da lista — `toggleCompletion`
 * do store, que delega a `@nv/core`. É o que garante o comportamento unificado
 * do PRD §14 por construção, e não por coincidência.
 */
import { calendarDate, formatDayMonth, horarioDaAtividade } from '@nv/core';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { rotaNegado } from '../grupo/permissaoLabels';
import { usePermissao, useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Card, Skeleton } from '../../ui/primitives';
import { mesclarDiaDoServidor, useCarregarDia } from './carregarDia';
import { SugestoesDaAtividade } from '../sugestoes/SugestoesDaAtividade';
import { FotosDaAtividade } from './FotosDaAtividade';
import { ComoChegar } from '../mapa/ComoChegar';
import { useItinerary } from './store';

const STATUS_LABEL = {
  planejada: 'Planejada',
  agora: 'Agora',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
} as const;

export function AtividadeScreen() {
  const { dia = '', atividade = '' } = useParams();
  const navigate = useNavigate();
  // Permissão real, do servidor — não um seletor de papel de demonstração.
  const canEditItinerary = usePermissao('edit_itinerary');
  const { members } = useTrip();

  // O detalhe carrega o dia por conta própria. Quem chega pela lista do dia
  // já tem o store cheio; quem chega por link direto — notificação, link
  // compartilhado, recarregar a página — não tem, e via "não encontrada" para
  // uma atividade que existe.
  const doDia = useCarregarDia(dia);
  const pronto = useItinerary((s) => s.ready && s.dayId === doDia.dayId);
  const activity = useItinerary((s) => s.activities.find((a) => a.id === atividade));
  const toggleCompletion = useItinerary((s) => s.toggleCompletion);

  // O dia veio do cache e a atividade não está nele: pode ser uma atividade
  // que outra pessoa criou depois da última mutação deste aparelho — o caso
  // da notificação de alerta (#84 no iPhone, Onda 1; #100 no Galaxy, Onda 3).
  // Só o servidor sabe; "não encontrada" só depois de ele responder (ou
  // falhar: sem rede, o cache é tudo que há).
  const [conferido, setConferido] = useState(false);
  useEffect(() => {
    if (!pronto || doDia.carregando || !doDia.dayId) return;
    if (activity || !doDia.doCache) {
      setConferido(true);
      return;
    }
    let vivo = true;
    void mesclarDiaDoServidor(doDia.dayId)
      .catch(() => undefined)
      .finally(() => {
        if (vivo) setConferido(true);
      });
    return () => {
      vivo = false;
    };
  }, [pronto, doDia.carregando, doDia.dayId, doDia.doCache, activity]);

  if (!activity) {
    return (
      <section className="screen">
        {pronto && !doDia.carregando && conferido ? (
          <>
            <p className="dia-footer">Atividade não encontrada.</p>
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => navigate(`/roteiro/${dia}`)}>
              Ver o dia
            </button>
          </>
        ) : (
          <Skeleton height={64} count={4} />
        )}
      </section>
    );
  }

  // A nota é campo da PRÓPRIA atividade. Participantes por atividade ainda não
  // têm endpoint de leitura; até terem, a seção não é exibida — melhor do que
  // listar gente que talvez não vá.
  const nota = activity.notes ?? null;
  const concluida = activity.status === 'concluida';

  const dataCivil = (() => {
    try {
      return calendarDate(dia);
    } catch {
      return null;
    }
  })();

  // D10: compartilhar é o compartilhador do aparelho; "Como chegar" abre o
  // app de mapas com o nome do lugar. Nenhum dos dois é tela interna.
  const podeCompartilhar = typeof navigator.share === 'function';
  const textoParaCompartilhar =
    `${activity.title} · ${dataCivil ? formatDayMonth(dataCivil) : dia} às ${horarioDaAtividade(activity)}` +
    (activity.locationName ? ` · ${activity.locationName}` : '');

  const tituloParaCompartilhar = activity.title;
  function compartilhar() {
    void navigator
      .share({ title: tituloParaCompartilhar, text: textoParaCompartilhar })
      // Cancelar o compartilhador não é erro.
      .catch(() => undefined);
  }


  return (
    <section className="screen ativ">
      <header className="dia-header">
        <span className="ativ-spacer" />
        {podeCompartilhar && (
          <button type="button" className="nv-btn nv-btn--chip" onClick={compartilhar}>
            <Icon name="compartilhar" size={18} />
            Compartilhar
          </button>
        )}
      </header>

      <div className="ativ-meta-row">
        <span className={`dia-status dia-status--${activity.status}`}>
          {STATUS_LABEL[activity.status]}
        </span>
        <p className="ativ-meta">
          {dataCivil ? formatDayMonth(dataCivil) : dia} · {horarioDaAtividade(activity)}
        </p>
      </div>

      <h1 className={concluida ? 'ativ-title ativ-title--feita' : 'ativ-title'}>{activity.title}</h1>

      {activity.locationName && (
        <Card className="ativ-local">
          <span className="ativ-local__icon" aria-hidden="true">
            <Icon name="local" size={20} />
          </span>
          <span className="ativ-local__texto">
            <span className="ativ-local__nome">{activity.locationName}</span>
            {activity.placeAddress && <span className="ativ-local__endereco">{activity.placeAddress}</span>}
          </span>
          <ComoChegar destino={activity} className="nv-btn nv-btn--chip ativ-local__ir">
            Como chegar
          </ComoChegar>
        </Card>
      )}

      <p className="ativ-quem" data-testid="ativ-quem">
        <Icon name="pessoas" size={16} />
        {activity.participantMemberIds && activity.participantMemberIds.length > 0
          ? `Vão: ${activity.participantMemberIds.map((id) => members.find((m) => m.memberId === id)?.name.split(/\s+/)[0] ?? 'alguém').join(', ')}`
          : 'Todo mundo vai'}
      </p>

      {activity.alertMinutesBefore != null && (
        <p className="ativ-aviso">
          <Icon name="notificacoes" size={16} />
          Aviso {activity.alertMinutesBefore >= 60 ? `${activity.alertMinutesBefore / 60} h` : `${activity.alertMinutesBefore} min`} antes, para quem ligou alertas
        </p>
      )}

      <FotosDaAtividade dia={dia} activityId={activity.id} coverMediaId={activity.coverMediaId} />

      {/* Sugestões do grupo nesta atividade (PO, 22/09/2026) — "Usar sugestão"
          fica ao lado das ações de foto, que é onde a pessoa já está. */}
      <SugestoesDaAtividade activityId={activity.id} />

      {nota && (
        <Card variant="inset" className="ativ-notas">
          <span className="ativ-rotulo">Notas</span>
          <p className="ativ-note">{nota}</p>
        </Card>
      )}

      {/* Refino final: ações fixas no rodapé, ao alcance do polegar. */}
      <div className="ativ-actions">
        <div className="ativ-actions__row">
          <button
            type="button"
            className={concluida ? 'nv-btn nv-btn--reopen' : 'nv-btn nv-btn--success'}
            disabled={activity.status === 'cancelada'}
            onClick={() => toggleCompletion(activity.id)}
          >
            {concluida ? (
              'Reabrir'
            ) : (
              <>
                <Icon name="concluir" size={18} />
                Concluir
              </>
            )}
          </button>

          {canEditItinerary ? (
            <button
              type="button"
              className="nv-btn nv-btn--secondary"
              onClick={() => navigate(`/roteiro/${dia}/${activity.id}/editar`)}
            >
              <Icon name="editar" size={18} />
              Editar
            </button>
          ) : (
            <button
              type="button"
              className="nv-btn nv-btn--secondary dia-reorder--locked"
              onClick={() => navigate(rotaNegado('edit_itinerary'))}
            >
              <Icon name="semPermissao" size={18} />
              Editar
            </button>
          )}
        </div>

        <button
          type="button"
          className="ativ-alteracoes"
          onClick={() => navigate(`/roteiro/${dia}/historico`)}
        >
          Ver alterações deste dia
        </button>
      </div>
    </section>
  );
}
